import {
  NFS_EXPORT_ACCESS_TYPE,
  NFS_EXPORT_ACCESS_TYPE_TEXT,
  NFS_EXPORT_SQUASH,
  NFS_EXPORT_SQUASH_SELECT_TEXT
} from '@/common/constants/nfsExport.constants';
import Vue from 'vue';
import { TaskState } from '@schema';
import { TaskName } from '@/common/constants/taskNames.constants';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';

export type NfsExportRaw = {
  access_type: NFS_EXPORT_ACCESS_TYPE
  anonymous_gid: number
  anonymous_uid: number
  clients: Array<unknown>
  name: string
  path: string
  security_types: Array<unknown>
  squash: NFS_EXPORT_SQUASH
}

export interface NfsExport extends NfsExportRaw {
  id: string,
  accessTypeText: string,
  squashText: string
}

export type NfsExportsState = {
  exports: Record<string, Array<NfsExport>>
}

export type NfsExportSet = {
  shareName: string,
  exports: Array<NfsExportRaw>
}

const nfsExportsState: NfsExportsState = {
  exports: {},
};

const nfsExportsGetters: CommonGetterTree<NfsExportsState> = {
  getItems: state => shareName => (state.exports[shareName] || []).map((exp: NfsExportRaw): NfsExport => ({
    ...exp,
    id: exp.name,
    accessTypeText: NFS_EXPORT_ACCESS_TYPE_TEXT[exp.access_type],
    squashText: NFS_EXPORT_SQUASH_SELECT_TEXT[exp.squash]
  })),
  getItemByName: (state, getters) => shareName => exportName => getters.getItems(shareName)
    .find((exportItem: NfsExport) => exportItem.name === exportName)
};

const nfsExportsMutations: CommonMutationTree<NfsExportsState> = {
  SET_NFS_EXPORTS(state, { shareName, exports }: NfsExportSet) {
    Vue.set(state.exports, shareName, exports || []);
  },
  ADD_NFS_EXPORT(state, { shareName, exportItem }) {
    if (state.exports[shareName]) {
      state.exports[shareName].push(exportItem);
    }
  },
  UPDATE_NFS_EXPORT(state, { shareName, exportItem: updatedExportItem }) {
    if (state.exports[shareName]) {
      const exportItem = state.exports[shareName].find(it => it.name === updatedExportItem.name);
      if (exportItem) {
        Object.assign(exportItem, updatedExportItem);
      }
    }
  },
  REMOVE_NFS_EXPORT(state, { shareName, exportName }) {
    if (state.exports[shareName]) {
      const index = state.exports[shareName].findIndex((exportItem: NfsExportRaw) => exportItem.name === exportName);
      if (index >= 0) {
        state.exports[shareName].splice(index, 1);
      }
    }
  }
};

const nfsExportsActions: CommonActionTree<NfsExportsState> = {
  FETCH_EXPORTS({ commit }, { shareName }) {
    return getClusterId()
      .then(clusterId => this.$api.get(`/api/v2/${clusterId}/nfs/shares/${shareName}/exports`))
      .then((res: FetchResponse) => {
        const nfsExportSet: NfsExportSet = {
          shareName,
          exports: res.data.data
        };
        commit('SET_NFS_EXPORTS', nfsExportSet);
      });
  },
  CREATE(_, { shareName, payload }) {
    return getClusterId()
      .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nfs/shares/${shareName}/exports`, {
        clients: [],
        security_types: ['sys'],
        ...payload
      }));
  },
  EDIT(_, { shareName, exportName, payload }) {
    return getClusterId()
      .then(clusterId => this.$api.put(`/api/v2/${clusterId}/nfs/shares/${shareName}/exports/${exportName}`, payload));
  },
  DELETE(_, { shareName, exportName }): Promise<any> {
    return getClusterId()
      .then(clusterId => this.$api.delete(`/api/v2/${clusterId}/nfs/shares/${shareName}/exports/${exportName}`));
  },
  DELETE_ITEMS({ dispatch }, { shareName, exports }): Promise<any> {
    return exports.reduce((promisesChain, exportName) => promisesChain.then(() => dispatch('DELETE', { shareName, exportName })), Promise.resolve());
  },
  socket_tasks({ commit }, { name, state, result, args }) {
    if (!args?.length) return;

    const [_clusterId, shareName, exportName] = args;

    if (name === TaskName.NFS_EXPORT_REMOVE && state === TaskState.SUCCESS) {
      commit('REMOVE_NFS_EXPORT', { shareName, exportName });
    }
    if (name === TaskName.NFS_EXPORT_ADD && state === TaskState.SUCCESS) {
      commit('ADD_NFS_EXPORT', { shareName, exportItem: result });
    }
    if (name === TaskName.NFS_EXPORT_UPDATE && state === TaskState.SUCCESS) {
      commit('UPDATE_NFS_EXPORT', { shareName, exportItem: result });
    }
  }
};

export default new class implements CommonStore<NfsExportsState, unknown> {
  name = 'nfsExports';
  state = nfsExportsState;
  getters = nfsExportsGetters;
  mutations = nfsExportsMutations;
  actions = nfsExportsActions;
  plural = data => data.data.map(item => ({ ...item, id: item.name }));
}();
